import React, { useRef, useState } from 'react';
import { MoreVertical, Download, Maximize2, FileSpreadsheet, Loader2 } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';

/**
 * Reusable 3-dots menu for any dashboard chart.
 *
 * Props:
 *   captureRef    – React ref pointing at the DOM node to rasterise for the JPG export.
 *   csvFilename   – stem (without extension) for the downloaded files.
 *   csvHeader     – array of column headers, e.g. ['Sitename', 'Account', ...].
 *   onFetchCsv    – async function that returns an array of plain-object rows
 *                   (keys must align with csvHeader by index when serialised
 *                   via mapRowToCells).
 *   mapRowToCells – fn(row) → array of cell strings/numbers, ordered the same
 *                   as csvHeader.
 *   title         – modal title.
 *   description   – modal description (optional).
 *   children      – the larger chart variant rendered inside the modal.
 *   testidPrefix  – data-testid stem (e.g. "monthly-spend-chart").
 */
export function ChartCardMenu({
  captureRef, csvFilename, csvHeader, onFetchCsv, mapRowToCells,
  title, description, children, testidPrefix,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // captureRef *for the modal* — caller passes the small one, we make a fresh
  // ref for the modal so JPG inside the modal captures the bigger chart.
  const modalRef = useRef(null);

  const downloadJpg = async (which) => {
    const node = which === 'modal' ? modalRef.current : captureRef?.current;
    if (!node) return;
    try {
      setBusy(true);
      const dataUrl = await htmlToImage.toJpeg(node, {
        backgroundColor: '#ffffff', quality: 0.95, pixelRatio: 2,
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${csvFilename}-${new Date().toISOString().slice(0, 10)}.jpg`;
      a.click();
    } catch (e) {
      console.error('JPG export failed', e);
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async () => {
    try {
      setBusy(true);
      const rows = await onFetchCsv();
      const lines = [csvHeader.join(',')];
      for (const r of rows) {
        const cells = mapRowToCells(r).map((v) => {
          if (v == null) return '';
          const s = String(v);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        });
        lines.push(cells.join(','));
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${csvFilename}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV export failed', e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-slate-400 hover:text-slate-700"
            data-testid={`${testidPrefix}-menu`}
            disabled={busy}
          >
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <MoreVertical className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="text-xs">
          <DropdownMenuItem
            onClick={() => downloadJpg('card')}
            data-testid={`${testidPrefix}-download-jpg`}
          >
            <Download className="mr-2 h-3.5 w-3.5" /> Download JPG
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setOpen(true)}
            data-testid={`${testidPrefix}-view-large`}
          >
            <Maximize2 className="mr-2 h-3.5 w-3.5" /> View large
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={downloadCsv}
            data-testid={`${testidPrefix}-download-csv`}
          >
            <FileSpreadsheet className="mr-2 h-3.5 w-3.5" /> Download data (CSV)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-5xl"
          data-testid={`${testidPrefix}-modal`}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? (
              <DialogDescription className="text-xs">{description}</DialogDescription>
            ) : null}
          </DialogHeader>
          <div ref={modalRef} className="h-[420px] w-full bg-white">
            {children}
          </div>
          <div className="mt-2 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadJpg('modal')}>
              <Download className="mr-2 h-4 w-4" /> JPG
            </Button>
            <Button size="sm" onClick={downloadCsv}>
              <FileSpreadsheet className="mr-2 h-4 w-4" /> CSV
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
